// Canonical session-aware instructor assignment for a single class.
//
// Reads and writes ONLY public.instructor_class_assignments through
// src/lib/staffing.ts — the same layer the Staffing Board uses, so the Class
// Editor, the Staffing Board, admin class cards and the public site can never
// disagree. `classes.instructor_id` is never written here; it is shown (when
// present) purely as legacy administrative metadata.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Info, Loader2, UserPlus } from "lucide-react";
import {
  assignInstructor,
  currentSessionFor,
  fetchStaffingData,
  findPayrollConflicts,
  instructorName,
  removeAssignment,
  resolveEligibleStartDate,
  staffingForClass,
  today,
  type AssignmentRow,
  type ClassRow,
  type EligibilityWindow,
  type InstructorRow,
  type SessionWindow,
} from "@/lib/staffing";
import AssignmentCompensationEditor from "@/components/admin/classes/AssignmentCompensationEditor";


interface Props {
  classId: string | null;
  slug: string;
  /** Legacy administrative contact stored on classes.instructor_id (read-only). */
  legacyContactLabel?: string | null;
  onLiveCountChange?: (count: number) => void;
}

export default function ClassSessionInstructorPanel({
  classId,
  slug,
  legacyContactLabel,
  onLiveCountChange,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cls, setCls] = useState<ClassRow | null>(null);
  const [session, setSession] = useState<SessionWindow | null>(null);
  const [live, setLive] = useState<AssignmentRow[]>([]);
  const [history, setHistory] = useState<AssignmentRow[]>([]);
  const [instructors, setInstructors] = useState<InstructorRow[]>([]);
  const [pick, setPick] = useState<string>("");
  const [effective, setEffective] = useState<string>(today());
  const [eligibility, setEligibility] = useState<EligibilityWindow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchStaffingData();
      const match =
        data.classes.find((c) => (classId && c.id === classId) || c.slug === slug) ?? null;
      setInstructors(data.instructors);
      setCls(match);
      if (!match) {
        setLive([]);
        setHistory([]);
        setEligibility(null);
        onLiveCountChange?.(0);
        return;
      }
      const win = currentSessionFor(match, data.periods);
      setSession(win);
      const scoped = data.assignments.filter(
        (a) => a.class_id === match.id || a.class_slug === match.slug,
      );
      const staffed = staffingForClass(data.assignments, match, win);
      setLive(staffed.rows);
      setHistory(scoped.filter((r) => !staffed.rows.some((x) => x.id === r.id)));
      onLiveCountChange?.(staffed.rows.length);

      // Payroll-safe default: the next meeting that has NOT been taught/paid.
      const window = await resolveEligibleStartDate(match, staffed.rows);
      setEligibility(window);
      setEffective(window.earliest);
    } catch (e) {
      console.error("[class staffing]", e);
      toast.error("Couldn't load the instructor assignments for this class.");
    } finally {
      setLoading(false);
    }
  }, [classId, slug, onLiveCountChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const tooEarly =
    !!eligibility?.settledThrough && effective <= (eligibility.settledThrough as string);

  const settledMessage = eligibility?.settledThrough
    ? `This class has teaching/payroll history through ${eligibility.settledThrough}. The earliest replacement date is ${eligibility.earliest}.`
    : null;

  const doAssign = async () => {
    if (!cls || !pick) return;
    if (tooEarly) {
      toast.error(settledMessage ?? "That effective date overlaps settled history.");
      return;
    }
    setBusy(true);
    try {
      const conflicts = await findPayrollConflicts(pick, cls.title, cls.slug, effective);
      if (conflicts.length) {
        // Paid meetings on/after the effective date would create duplicate pay.
        toast.error(
          `Paid payroll already exists on ${conflicts.flatMap((c) => c.dates).join(", ")}. Nothing was changed — move the effective date past those meetings.`,
        );
        return;
      }
      await assignInstructor({
        cls,
        session,
        instructorId: pick,
        effectiveDate: effective,
        supersede: live,
      });
      toast.success("Instructor assigned to this session.");
      setPick("");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't save the assignment.");
    } finally {
      setBusy(false);
    }
  };

  const doRemove = async (row: AssignmentRow) => {
    if (!cls) return;
    if (tooEarly) {
      toast.error(settledMessage ?? "That effective date overlaps settled history.");
      return;
    }
    if (!confirm("End this assignment? History and any earned payroll are preserved.")) return;
    setBusy(true);
    try {
      const conflicts = await findPayrollConflicts(row.instructor_id, cls.title, cls.slug, effective);
      if (conflicts.length) {
        toast.error(
          `Paid payroll exists on ${conflicts.flatMap((c) => c.dates).join(", ")}. Nothing was changed — end the assignment after those meetings.`,
        );
        return;
      }
      await removeAssignment(row, effective);
      toast.success("Assignment ended. The record was kept for history.");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't end the assignment.");
    } finally {
      setBusy(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading instructor assignments…
      </div>
    );
  }

  if (!cls) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Save the class first</AlertTitle>
        <AlertDescription>
          Session instructors can be assigned once the class record exists.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium">Session instructor (canonical)</div>
          <Badge variant="outline">{session?.label ?? "No current session"}</Badge>
        </div>

        {live.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Instructor Needed — no one is assigned to this session yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {live.map((a) => {
              const person = instructors.find((i) => i.id === a.instructor_id);
              return (
                <li key={a.id} className="rounded border p-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium">{instructorName(person)}</div>
                      <div className="text-xs text-muted-foreground">
                        {a.session_label ?? "Session TBD"} · {a.status} ·{" "}
                        {a.start_date ?? "no start"} → {a.end_date ?? "open"}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => doRemove(a)}>
                      Remove
                    </Button>
                  </div>
                  <AssignmentCompensationEditor assignment={a} onSaved={load} />
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-wrap items-end gap-2 pt-1">
          <div className="min-w-[220px] flex-1">
            <div className="text-xs text-muted-foreground mb-1">Assign instructor</div>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an instructor" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {instructors.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {instructorName(i)}
                    {i.status ? ` · ${i.status}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Effective date</div>
            <Input
              type="date"
              value={effective}
              min={eligibility?.earliest}
              onChange={(e) => setEffective(e.target.value || eligibility?.earliest || today())}
              className="w-[160px]"
            />
          </div>
          <Button size="sm" disabled={!pick || busy || tooEarly} onClick={doAssign}>
            <UserPlus className="h-4 w-4 mr-1" /> Assign
          </Button>
        </div>

        {settledMessage && (
          <Alert variant={tooEarly ? "destructive" : "default"}>
            <Info className="h-4 w-4" />
            <AlertTitle>Protected teaching history</AlertTitle>
            <AlertDescription className="text-xs space-y-2">
              <p>{settledMessage}</p>
              {tooEarly && eligibility && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEffective(eligibility.earliest)}
                >
                  Use {eligibility.earliest}
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        <p className="text-[11px] text-muted-foreground">
          Assignments are effective-dated and default to the next meeting that has not been taught
          or paid. Earlier meetings — and the payroll earned for them — are never rewritten.
        </p>

      </div>

      {history.length > 0 && (
        <div className="rounded-md border p-4">
          <div className="text-sm font-medium mb-2">Assignment history</div>
          <ul className="text-xs space-y-1">
            {history.map((a) => {
              const person = instructors.find((i) => i.id === a.instructor_id);
              return (
                <li key={a.id} className="flex items-center justify-between gap-2">
                  <span>
                    {instructorName(person)} — {a.session_label ?? "—"}
                  </span>
                  <span className="text-muted-foreground">
                    {a.status} · {a.start_date ?? "—"} → {a.end_date ?? "—"}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {legacyContactLabel && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Legacy class contact on file</AlertTitle>
          <AlertDescription>
            {legacyContactLabel} is stored as administrative metadata only. It does not staff a
            session and is ignored by payroll and the public site.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
